import { useGetRlEnvironmentResultQuery } from '@gql/gql-types';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';

import { AssessmentFeedback } from '../../interview/components/assessment-feedback';

const MAX_SCORE = 5;
const RESULT_POLL_MS = 5000;

// Component names come from the template's response schema, which admins edit,
// so they are humanized rather than mapped to a fixed set of labels.
const humanize = (name: string) =>
  name.replace(/[_-]+/g, ' ').replace(/^./, (first) => first.toUpperCase());

const cardSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  backgroundColor: 'background.paper',
  p: 2,
};

export const ResultsPanel = ({ assessmentId }: { assessmentId: string }) => {
  const { data, error, stopPolling } = useGetRlEnvironmentResultQuery({
    pollInterval: RESULT_POLL_MS,
  });
  const result = data?.getRlEnvironmentResult;

  useEffect(() => {
    if (result) {
      stopPolling();
    }
  }, [result, stopPolling]);

  if (!result) {
    const pendingCopy = error
      ? 'Your submission is saved. Scoring is taking longer than usual.'
      : 'This takes a couple of minutes, and this page updates itself.';
    return (
      <Container sx={{ pt: 8, pb: 6, maxWidth: 'sm', textAlign: 'center' }}>
        <Stack spacing={3} alignItems='center'>
          <CircularProgress />
          <Box>
            <Typography variant='h6' gutterBottom>
              Scoring your environment
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {pendingCopy}
            </Typography>
          </Box>
        </Stack>
      </Container>
    );
  }

  return (
    <Container sx={{ pt: 4, pb: 6, maxWidth: 'md' }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant='h5' gutterBottom>
            You scored {result.score ?? '—'} out of {MAX_SCORE}
          </Typography>
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Chip
              size='small'
              color={result.passed ? 'success' : 'error'}
              label={result.passed ? 'Passed' : 'Did not pass'}
            />
            <Typography variant='body2' color='text.secondary'>
              {result.roundCount} agent{' '}
              {result.roundCount === 1 ? 'round' : 'rounds'}
            </Typography>
          </Stack>
        </Box>

        {result.rationale ? (
          <Typography variant='body1'>{result.rationale}</Typography>
        ) : null}

        <Box sx={cardSx}>
          <Typography variant='overline' sx={{ letterSpacing: 1 }}>
            Checks
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {result.checks.map((check) => (
              <Stack
                key={check.id}
                direction='row'
                spacing={1}
                alignItems='center'
              >
                {check.passed ? (
                  <CheckCircleOutlineIcon color='success' fontSize='small' />
                ) : (
                  <HighlightOffIcon color='error' fontSize='small' />
                )}
                <Typography variant='body2'>{check.label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        {result.components.length > 0 ? (
          <Box sx={cardSx}>
            <Typography variant='overline' sx={{ letterSpacing: 1 }}>
              Reviewer breakdown
            </Typography>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {result.components.map((component) => (
                <Box key={component.name}>
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='baseline'
                    justifyContent='space-between'
                  >
                    <Typography variant='subtitle2'>
                      {humanize(component.name)}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {component.score} / {MAX_SCORE}
                    </Typography>
                  </Stack>
                  <Typography variant='body2' color='text.secondary'>
                    {component.comment}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        ) : null}

        <Box sx={{ textAlign: 'center' }}>
          <AssessmentFeedback
            assessmentId={assessmentId}
            copy={{
              title: 'Thanks for taking the assessment',
              ratingLabel: 'Rate your experience',
              commentLabel: 'Describe your experience',
            }}
          />
        </Box>
      </Stack>
    </Container>
  );
};
